/*
 * DecisionNode — refined card-style node for conditional branching.
 * Amber/gold bordered card with diamond icon, YES/NO output handles
 * at the bottom. Much more readable than the old rotated diamond.
 */

import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { DecisionIcon } from './NodeIcons';
import './DecisionNode.css';

function getConditionSummary(config) {
  if (!config || !config.conditions || config.conditions.length === 0) {
    return 'No conditions';
  }
  const count = config.conditions.length;
  const logic = config.logic || 'AND';
  if (count === 1) {
    const c = config.conditions[0];
    return `${c.field || '?'} ${c.operator || '?'} ${c.value || ''}`.trim();
  }
  return `${count} conditions (${logic})`;
}

const DecisionNode = memo(({ data, selected }) => {
  const { label, config, executionStatus } = data;
  const summary = getConditionSummary(config);
  const execClass = executionStatus ? `exec-${executionStatus}` : '';

  return (
    <div className={`wf-decision ${selected ? 'selected' : ''} ${execClass}`}>
      {executionStatus === 'completed' && <span className="wf-decision-exec-badge completed">✓</span>}
      {executionStatus === 'failed' && <span className="wf-decision-exec-badge failed">✕</span>}
      {executionStatus === 'running' && (
        <div className="wf-decision-exec-badge running"><span className="wf-decision-spinner" /></div>
      )}

      <Handle type="target" position={Position.Top} id="in" />

      <div className="wf-decision-content">
        <div className="wf-decision-header">
          <span className="wf-decision-icon-wrap"><DecisionIcon /></span>
          <span className="wf-decision-label">{label || 'Decision'}</span>
        </div>
        <div className="wf-decision-summary">{summary}</div>
      </div>

      <div className="wf-decision-handles">
        <div className="wf-decision-handle-group yes">
          <Handle type="source" position={Position.Bottom} id="yes" className="wf-handle-yes" />
          <span className="wf-decision-handle-tag yes">YES</span>
        </div>
        <div className="wf-decision-handle-group no">
          <Handle type="source" position={Position.Bottom} id="no" className="wf-handle-no" />
          <span className="wf-decision-handle-tag no">NO</span>
        </div>
      </div>
    </div>
  );
});

DecisionNode.displayName = 'DecisionNode';

export default DecisionNode;
